/**
 * ETL Dashboard Redux Slice
 * Manages state for ETL configurations, loading states, and user interactions
 *
 * ARCHITECTURAL PATTERN (MANDATORY):
 * - Slice files MUST only contain createSlice with reducers
 * - Thunks MUST be in separate action files (etl.action.ts)
 * - Reference: curriculum.slice.ts for canonical example
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import type {
  IETLDashboardState,
  IETLConfigurationsResponse,
} from '../../interface/etlDashboard.interface';
import { fetchETLConfigurations, completeETLSetup, fetchETLStatus } from '../action/etl.action';

// Initial State
const initialState: IETLDashboardState = {
  configurations: [],
  setupComplete: false,
  hasAvailableQuickSetupTemplates: false,
  showEtlPayWallBanner: false,
  loading: false,
  error: null,
  acknowledgeChecked: false,
  expandedSections: {
    platformFeatures: true,
    setupInstructions: true,
  },
};

// Slice (Reducers Only)
const etlSlice = createSlice({
  name: 'etl',
  initialState,
  reducers: {
    /**
     * Toggle acknowledgement checkbox
     */
    toggleAcknowledge: (state) => {
      state.acknowledgeChecked = !state.acknowledgeChecked;
    },

    /**
     * Toggle expandable sections
     */
    toggleSection: (state, action: PayloadAction<'platformFeatures' | 'setupInstructions'>) => {
      state.expandedSections[action.payload] = !state.expandedSections[action.payload];
    },

    /**
     * Reset UI state on navigation — preserves setupComplete
     * Called on ETL Dashboard mount to reset checkbox/sections
     */
    resetETLState: (state) => {
      state.acknowledgeChecked = initialState.acknowledgeChecked;
      state.expandedSections = { ...initialState.expandedSections };
      state.loading = initialState.loading;
      state.error = initialState.error;
    },

    /**
     * Manually set setupComplete (used after completeETLSetup success)
     */
    setSetupComplete: (state, action: PayloadAction<boolean>) => {
      state.setupComplete = action.payload;
    },

    /**
     * Hide the ETL paywall banner (used after user clicks "Get Started")
     */
    hideEtlPayWallBanner: (state) => {
      state.showEtlPayWallBanner = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch Configurations
    builder
      .addCase(fetchETLConfigurations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchETLConfigurations.fulfilled,
        (state, action: PayloadAction<IETLConfigurationsResponse>) => {
          state.loading = false;
          // Ensure configurations is always an array
          state.configurations = Array.isArray(action.payload.data.configurations)
            ? action.payload.data.configurations
            : [];
          // Store setupComplete from API response (source of truth)
          state.setupComplete = action.payload.data.setupComplete ?? false;
          // Quick Setup stays reachable while any curriculum template is still unconfigured
          state.hasAvailableQuickSetupTemplates =
            action.payload.data.hasAvailableQuickSetupTemplates ?? false;
        },
      )
      .addCase(fetchETLConfigurations.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_etl_configurations', 'Failed to fetch ETL configurations.');
        // Reset configurations to empty array on error
        state.configurations = [];
      });

    // Complete Setup
    builder
      .addCase(completeETLSetup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeETLSetup.fulfilled, (state) => {
        state.loading = false;
        // Mark setup as complete — no token logic needed
        state.setupComplete = true;
      })
      .addCase(completeETLSetup.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_complete_etl_setup', 'Failed to complete ETL setup.');
      });

    // Fetch ETL Status (lightweight - paywall banner flag)
    builder.addCase(fetchETLStatus.fulfilled, (state, action) => {
      state.setupComplete = action.payload.data.setupComplete ?? false;
      state.showEtlPayWallBanner = action.payload.data.showEtlPayWallBanner ?? false;
    });
  },
});

export const {
  toggleAcknowledge,
  toggleSection,
  resetETLState,
  setSetupComplete,
  hideEtlPayWallBanner,
} = etlSlice.actions;
export default etlSlice.reducer;
